import { useState, useEffect, useCallback } from 'react';
import { KeyRound, RefreshCw, ShieldAlert, Copy, Clock, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { securityAPI } from '@/services/api';

const dateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
  });
};

export default function SecurityCodePage() {
  const [code, setCode] = useState(null);
  const [usages, setUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [codeRes, usageRes] = await Promise.all([
        securityAPI.getCode(),
        securityAPI.codeUsages(50)
      ]);
      setCode(codeRes.data?.exists ? codeRes.data : null);
      setUsages(usageRes.data || []);
    } catch (error) {
      toast({
        title: 'Access denied',
        description: error.response?.data?.detail || 'Only HQ Super Admin can manage the security code',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await securityAPI.regenerate();
      setCode(res.data);
      setConfirmOpen(false);
      toast({ title: 'New code generated', description: `Active code: ${res.data.code}` });
      load();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to generate a code',
        variant: 'destructive'
      });
    } finally {
      setRegenerating(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(code.code);
    toast({ title: 'Copied', description: 'Security code copied to clipboard' });
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="security-code-page">
      <Toaster />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-orange-500" /> Security Code
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          One active 6-character code authorises margin reductions in GRN and edits to frozen Product Master pricing.
          It expires 24 hours after it is generated.
        </p>
      </div>

      <Card className="bg-white border-gray-200 shadow-sm mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-800">Active code</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : !code ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <p className="text-gray-500">No security code has been generated yet.</p>
              <Button onClick={() => setConfirmOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="generate-code-btn">
                <KeyRound className="w-4 h-4 mr-2" /> Generate code
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Code</p>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-mono text-3xl tracking-[0.3em] px-4 py-2 rounded-lg border ${
                      code.is_expired
                        ? 'bg-red-50 border-red-200 text-red-500 line-through'
                        : 'bg-orange-50 border-orange-200 text-orange-700'
                    }`}
                    data-testid="active-security-code"
                  >
                    {code.code}
                  </span>
                  <Button variant="ghost" size="icon" onClick={copy} title="Copy" data-testid="copy-code-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <p className="text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Generated {dateTime(code.generated_at)} by {code.generated_by_name || '-'}
                </p>
                <p className={code.is_expired ? 'text-red-600 font-medium' : 'text-gray-500'}>
                  {code.is_expired ? 'Expired' : 'Expires'} {dateTime(code.expires_at)}
                </p>
              </div>
              <Button
                onClick={() => setConfirmOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 ml-auto"
                data-testid="regenerate-code-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-800 flex items-center gap-2">
            <History className="w-4 h-4" /> Code usage log
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Used by</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Context</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-400 py-8">No code has been used yet</TableCell>
                  </TableRow>
                ) : usages.map(u => (
                  <TableRow key={u.id} data-testid={`code-usage-${u.id}`}>
                    <TableCell className="text-sm text-gray-600">{dateTime(u.used_at)}</TableCell>
                    <TableCell className="text-sm">{u.used_by_name || '-'}</TableCell>
                    <TableCell className="text-sm capitalize">{(u.purpose || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-sm text-gray-500">{u.context || '-'}</TableCell>
                    <TableCell>
                      {u.success ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Accepted</Badge>
                      ) : (
                        <Badge className="bg-red-50 text-red-700 border border-red-200">Rejected</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-orange-500" /> Generate a new security code?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The current code stops working immediately. Anyone holding the old code will need the new one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); regenerate(); }}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-regenerate-btn"
            >
              {regenerating ? 'Generating...' : 'Generate new code'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
